import type { AnalyticsEventParams } from '@/shared/analytics'
import { hasAcceptedAnalyticsConsent } from '@/shared/analytics/consent'
import { trackAnalyticsEvent } from '@/shared/analytics'

type GtmEvent =
  | 'gtm_demo_started'
  | 'gtm_results_viewed'
  | 'begin_checkout'
  | 'gtm_checkout_amount_selected'
  | 'gtm_checkout_continue'
  | 'gtm_test_payment_completed'
  | 'purchase'
  | 'gtm_workspace_activated'
  | 'gtm_project_created'
  | 'gtm_search_started'
  | 'gtm_send_requested'

const seen = new Set<string>()

/** IDs are used locally for session deduplication, never sent to analytics. */
export function trackGtmEvent(
  event: GtmEvent,
  params: AnalyticsEventParams = {},
  onceKey?: string,
): void {
  if (!hasAcceptedAnalyticsConsent()) return
  const key = onceKey ? `gtm_analytics:${event}:${onceKey}` : null
  if (key) {
    if (seen.has(key)) return
    try {
      if (sessionStorage.getItem(key)) return
    } catch {
      // Analytics must not break the product when browser storage is blocked.
    }
  }
  trackAnalyticsEvent(event, { product: 'laplace_gtm', ...params })
  if (key) {
    seen.add(key)
    try {
      sessionStorage.setItem(key, '1')
    } catch {
      // The in-memory guard still prevents duplicates until the next reload.
    }
  }
}

/**
 * GA4 `purchase` for a payment the server confirmed with a live provider.
 * Never for test payments. `transaction_id` is the payment id (GA4 needs it
 * to deduplicate); it is also kept in localStorage so a reload or another
 * tab never reports the same payment twice.
 */
export function trackGtmPurchase(payment: {
  paymentId: string
  amountCents: number
  provider: string
  isTest: boolean
  status: string
  context: 'demo' | 'app'
}): void {
  if (payment.isTest || payment.status !== 'completed') return
  const key = `gtm_purchase:${payment.paymentId}`
  try {
    if (localStorage.getItem(key)) return
  } catch {
    // Fall back to the per-session guard in trackGtmEvent.
  }
  trackGtmEvent(
    'purchase',
    {
      transaction_id: payment.paymentId,
      currency: 'USD',
      value: payment.amountCents / 100,
      payment_provider: payment.provider,
      checkout_context: payment.context,
    },
    payment.paymentId,
  )
  try {
    if (hasAcceptedAnalyticsConsent()) localStorage.setItem(key, '1')
  } catch {
    // Storage blocked: the session guard still applies.
  }
}
